import { useMemo } from 'react';
import usdcTokenIMG from '@/assets/imgs/tokens/usdc.svg';
// Token icons
import usdtTokenIMG from '@/assets/imgs/tokens/usdt.svg';
import {
  bitcoinChain,
  DEFAULT_CHAIN,
  SUPPORTED_CHAINS,
  USDC_DATA,
  USDT_DATA,
} from '@package/smart-wallet/constants';
import { useMagic, useSmartWallet } from '@package/smart-wallet/providers';
import { useChainStore } from '@package/smart-wallet/store';
import { useAccount, useBalance } from 'wagmi';

import { useGetBitcoinBalance } from './useGetBitcoinBalance';

export const useGetTokenBalances = (props?: { allTokens?: boolean }) => {
  const { chain } = useAccount();
  const { bitcoin } = useMagic();
  const localChainId = useChainStore((state) => state.chainId);
  const { smartAccountAddress, isLoading: isSmartWalletLoading } =
    useSmartWallet();
  const { data: bitcoinBalance } = useGetBitcoinBalance(bitcoin.address);

  const chainId = useMemo(() => {
    return localChainId !== bitcoinChain.id
      ? (chain?.id ?? DEFAULT_CHAIN.id)
      : bitcoinChain.id;
  }, [chain?.id, localChainId]);
  const currentChain = SUPPORTED_CHAINS[chainId];

  // USDT Balance (primary)
  const {
    data: usdtData,
    isLoading: isUsdtLoading,
    refetch: refetchUsdt,
  } = useBalance({
    address: smartAccountAddress,
    token: (USDT_DATA as any)[chainId]?.contractAddress,
  });

  // Native token balance
  const {
    data: nativeData,
    isLoading: isNativeLoading,
    refetch: refetchNative,
  } = useBalance({
    address: smartAccountAddress,
    chainId,
  });

  // USDC Balance
  const {
    data: usdcData,
    isLoading: isUsdcLoading,
    refetch: refetchUsdc,
  } = useBalance({
    address: smartAccountAddress,
    token: (USDC_DATA as any)[chainId]?.contractAddress,
  });

  const isLoading = useMemo(() => {
    return (
      isSmartWalletLoading || isUsdtLoading || isNativeLoading || isUsdcLoading
    );
  }, [isSmartWalletLoading, isUsdtLoading, isNativeLoading, isUsdcLoading]);

  const tokens = useMemo(() => {
    if (!props?.allTokens) return [];
    return [
      {
        symbol: usdtData?.symbol || 'USDT',
        amount: usdtData?.value.toString() || '0',
        decimals:
          usdtData?.decimals || (USDT_DATA as any)[chainId]?.decimals || 18,
        icon: usdtTokenIMG,
        isPrimary: true,
      },
      {
        symbol:
          nativeData?.symbol ??
          currentChain?.chain.nativeCurrency.symbol ??
          DEFAULT_CHAIN.nativeCurrency.symbol,
        amount: nativeData?.value.toString() || '0',
        // An undefined decimals would silently fall back to the formatter's
        // 6-decimal default and mis-scale the native balance by 1e12.
        decimals:
          nativeData?.decimals ??
          currentChain?.chain.nativeCurrency.decimals ??
          18,
        icon: currentChain?.nativeIcon,
        isPrimary: false,
      },
      {
        symbol: usdcData?.symbol || 'USDC',
        amount: usdcData?.value.toString() || '0',
        decimals:
          usdcData?.decimals || (USDC_DATA as any)[chainId]?.decimals || 18,
        icon: usdcTokenIMG,
        isPrimary: false,
      },
    ];
  }, [usdtData, nativeData, usdcData, chainId, currentChain]);

  const mainToken = useMemo(() => {
    if (chainId === bitcoinChain.id) {
      return {
        symbol: bitcoinChain.nativeCurrency.symbol,
        decimals: bitcoinChain.nativeCurrency.decimals,
        value: bitcoinBalance,
      };
    }

    return {
      symbol: usdtData?.symbol,
      decimals: usdtData?.decimals,
      value: usdtData?.value.toString(),
    };
  }, [chainId, usdtData, bitcoinBalance]);

  const refetchAll = () => {
    refetchUsdt();
    refetchNative();
    refetchUsdc();
  };

  return {
    tokens,
    isLoading,
    refetchAll,
    // Primary USDT data for backwards compatibility
    tokenSymbol: mainToken?.symbol,
    tokenAmount: mainToken?.value,
    tokenDecimals: mainToken?.decimals,
  };
};
